import React from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { chain } from 'lodash';

import Icon from '../../../components/icon/Icon';
import BlockBookingSlotsCalendar from './BlockBookingSlotsCalendar';
import Table from '../../../components/table/Table';
import THead from '../../../components/table/THead/THead';
import TR from '../../../components/table/TR/TR';
import TH from '../../../components/table/TH/TH';
import TBody from '../../../components/table/TBody/TBody';
import { generateEntityFetchListAsyncAction } from '../../../core/state/entity/generateEntityAsyncActions';
import EntityName from '../../../core/enums/EntityName';
import { getBlockedSlots } from '../../../core/state/blocked-slots/selectors';
import { RootState } from '../../../core/state/state';
import Button from '../../../components/form/button/Button';
import { StateStatus } from '../../../core/enums/StateStatus';
import BlockBookingSlotModal from '../modals/block-delivery-zone/BlockBookingSlotModal';
import { getDeliveryZones } from '../../../core/state/zones/asyncActions';
import CancelAllBookingsModal from '../modals/cancel-all-bookings/CancelAllBookingsModal';
import BlockBookingSlotsItem from './BlockBookingSlotsItem';
import ConfirmModal from '../../components/confirm-modal/ConfirmModal';
import { unblockAllDates } from '../../../core/state/bookings/asyncActions';
import { spawnGlobalNotification } from '../../../core/state/global-notifications/asyncActions';
import { GlobalNotificationTypes } from '../../../core/enums/GlobalNotificationTypes';
import { NotificationPosition } from '../../../core/state/global-notifications/types';
import { isReadAllRole } from '../../../core/state/user/selectors';

import 'react-datepicker/dist/react-datepicker.css';
import './BlockBookingSlots.scss';

const BlockBookingSlots: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const fetchBlockedSlots = React.useMemo(() => generateEntityFetchListAsyncAction(EntityName.BlockedSlot), []);
  const deliveryZones = useSelector((state: RootState) => state.zones.deliveryZones);
  const params = useParams<{ projectId: string }>();
  const projectId = Number(params.projectId);
  const blockedSlots = useSelector((state: RootState) => Object.values(getBlockedSlots(state)));
  const blockedSlotsSorted = chain(blockedSlots)
    .uniqWith((first, second) => {
      return first.blockedAllDay && second.blockedAllDay && first.blockedDayStr === second.blockedDayStr;
    })
    .orderBy(['blockedDay', 'timeSlot'], ['asc'])
    .value();
  const stateStatus = useSelector((state: RootState) => state.blockedSlots.status);
  const [isModalOpen, setIsModalOpen] = React.useState<boolean>(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = React.useState<boolean>(false);
  const [isUnblockBookingModalOpen, setIsUnblockBookingModalOpen] = React.useState<boolean>(false);
  const [selectedDate, setSelectedDate] = React.useState<Date | null>(null);
  const isUserReader = useSelector(isReadAllRole);

  React.useEffect(() => {
    dispatch(fetchBlockedSlots({ projectId }));
    dispatch(getDeliveryZones(projectId));
  }, [dispatch, fetchBlockedSlots, projectId]);

  const handleDateChange = (date: Date) => {
    if (!deliveryZones) {
      dispatch(
        spawnGlobalNotification({
          type: GlobalNotificationTypes.Error,
          postition: NotificationPosition.Top,
          message: t('projectSettings.blockBookingSlots.noActiveZones')
        })
      );
    } else if (deliveryZones && !isUserReader) {
      setSelectedDate(date);
      setIsModalOpen(true);
    }
  };

  const closeModalDialog = () => {
    dispatch(
      spawnGlobalNotification({
        type: GlobalNotificationTypes.Info,
        postition: NotificationPosition.Top,
        title: t('projectSettings.blockBookingSlots.unblockAllDates.cancel.title'),
        message: t('projectSettings.blockBookingSlots.unblockAllDates.cancel.text')
      })
    );
    setIsUnblockBookingModalOpen(false);
  };

  return (
    <div className="block-booking-slots">
      {stateStatus === StateStatus.Busy && <div className="toolbar__loader block-booking-slots__loader" />}
      <div className="mb-5">
        <div className="form-item__description">{t('projectSettings.blockBookingSlots.operatingHours.description')}</div>
        <div className="form-item__info">
          <Icon className="form-item__info-icon" icon="info" size="small" svg />
          {t('projectSettings.blockBookingSlots.operatingHours.info')}
        </div>
      </div>
      <div>
        <div className="block-booking-slots__calendar-wrapper">
          <BlockBookingSlotsCalendar blockedSlots={blockedSlots} disabled={stateStatus === StateStatus.Busy} handleDateChange={handleDateChange} />
          <div className="block-booking-slots__actions">
            <span className="block-booking-slots__actions-title">{t('projectSettings.blockBookingSlots.table.title')}</span>
            {blockedSlots.length > 1 && !isUserReader && (
              <Button className="block-booking-slots__actions-btn" buttonTypes={['red-outline']} onClick={() => setIsUnblockBookingModalOpen(true)}>
                {t('projectSettings.blockBookingSlots.button.unblockAll')}
              </Button>
            )}
          </div>
          <div className="block-booking-slots__table">
            <Table>
              <THead>
                <TR>
                  <TH>{t('projectSettings.blockBookingSlots.table.blockedDate')}</TH>
                  <TH>{t('projectSettings.blockBookingSlots.table.blockedTime')}</TH>
                  <TH>{t('projectSettings.blockBookingSlots.table.blockedZone')}</TH>
                  <TH>{t('common.comment')}</TH>
                  <TH>{t('common.createdBy')}</TH>
                  <TH>{t('common.action')}</TH>
                </TR>
              </THead>
              <TBody>{blockedSlotsSorted.length > 0 && blockedSlotsSorted.map((slot) => <BlockBookingSlotsItem key={slot.id} slot={slot} />)}</TBody>
            </Table>
            {!blockedSlotsSorted ||
              (blockedSlotsSorted.length < 1 && (
                <div className="block-booking-slots__empty-list">
                  <span className="block-booking-slots__empty-list-title">{t('projectSettings.blockBookingSlots.noData.text')}</span>
                  <span>{t('projectSettings.blockBookingSlots.noData.description')}</span>
                </div>
              ))}
          </div>
        </div>
      </div>
      {isModalOpen && selectedDate && (
        <BlockBookingSlotModal
          dateFrom={selectedDate}
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          handleBookingModal={() => setIsCancelModalOpen(true)}
        />
      )}
      {isCancelModalOpen && <CancelAllBookingsModal type="BlockingSlot" isOpen={isCancelModalOpen} closeModal={() => setIsCancelModalOpen(false)} />}
      <ConfirmModal
        height={196}
        titleText={t('projectSettings.blockBookingSlots.unblockAllDates.title')}
        contentText={t('projectSettings.blockBookingSlots.unblockAllDates.text')}
        isOpen={isUnblockBookingModalOpen}
        onCancel={() => closeModalDialog()}
        onConfirm={async () => {
          await dispatch(unblockAllDates(projectId));
          await dispatch(fetchBlockedSlots({ projectId }));
          setIsUnblockBookingModalOpen(false);
          dispatch(
            spawnGlobalNotification({
              type: GlobalNotificationTypes.Success,
              postition: NotificationPosition.Top,
              message: t('projectSettings.blockBookingSlots.unblockAllDates.notification')
            })
          );
        }}
      />
    </div>
  );
};

export default BlockBookingSlots;
